import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { articles } from "@/mocks/articles";

export default function ArticleList() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const [featured, ...rest] = articles;

  return (
    <section className="py-20 bg-[#faf8f4]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-16">

        {/* Featured Article */}
        <div
          className={`mb-16 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-px bg-[#8b6f47]"></span>
            <span
              className="text-[#8b6f47] text-[1rem] tracking-[0.3em] font-medium"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              精選文章
            </span>
          </div>

          <div
            className="group grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden cursor-pointer bg-white"
            onClick={() => { navigate(`/knowledge/${featured.id}`); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          >
            {/* Image */}
            <div className="w-full h-72 lg:h-auto overflow-hidden">
              <img
                src={featured.coverImg}
                alt={featured.title}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            {/* Content */}
            <div className="p-10 md:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-5">
                <span
                  className="px-3 py-1 rounded-full text-[0.875rem] font-medium"
                  style={{
                    color: featured.tagColor,
                    backgroundColor: featured.tagBg,
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {featured.tag}
                </span>
                <span
                  className="text-[#a89880] text-[1rem]"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  {featured.readTime}
                </span>
              </div>
              <h2
                className="text-[#2d2520] font-bold mb-4 leading-[1.4] group-hover:text-[#8b6f47] transition-colors"
                style={{
                  fontSize: "clamp(1.5rem, 2.5vw, 2.125rem)",
                  fontFamily: "'Noto Serif TC', serif",
                }}
              >
                {featured.title}
              </h2>
              <p
                className="text-[#7a6a5a] leading-[1.75] mb-8"
                style={{
                  fontSize: "1.1875rem",
                  fontFamily: "'Noto Serif TC', serif",
                }}
              >
                {featured.summary}
              </p>
              <div className="flex items-center gap-2 text-[#8b6f47] font-medium text-[1rem]">
                <span style={{ fontFamily: "'Noto Serif TC', serif" }}>閱讀全文</span>
                <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Other Articles */}
        <div className="flex items-center gap-3 mb-8">
          <span className="w-10 h-px bg-[#8b6f47]"></span>
          <span
            className="text-[#8b6f47] text-[1rem] tracking-[0.3em] font-medium"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            更多文章
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {rest.map((article, i) => (
            <article
              key={article.id}
              className={`group bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-1000`}
              style={{
                transitionDelay: `${i * 120}ms`,
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(24px)",
              }}
              onClick={() => { navigate(`/knowledge/${article.id}`); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            >
              {/* Image */}
              <div className="w-full h-52 overflow-hidden">
                <img
                  src={article.coverImg}
                  alt={article.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              {/* Content */}
              <div className="p-7">
                <div className="flex items-center justify-between mb-4">
                  <span
                    className="px-3 py-1 rounded-full text-[0.875rem] font-medium"
                    style={{
                      color: article.tagColor,
                      backgroundColor: article.tagBg,
                      fontFamily: "'Noto Serif TC', serif",
                    }}
                  >
                    {article.tag}
                  </span>
                  <span
                    className="text-[#a89880] text-[1rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    {article.readTime}
                  </span>
                </div>
                <h3
                  className="text-[#2d2520] font-bold mb-3 leading-[1.45] group-hover:text-[#8b6f47] transition-colors"
                  style={{
                    fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)",
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {article.title}
                </h3>
                <p
                  className="text-[#7a6a5a] leading-[1.75] mb-5 line-clamp-3"
                  style={{
                    fontSize: "1.1875rem",
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {article.summary}
                </p>
                <div className="flex items-center gap-2 text-[#8b6f47] font-medium text-[1rem]">
                  <span style={{ fontFamily: "'Noto Serif TC', serif" }}>閱讀全文</span>
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
